import { MediaController } from 'media-chrome';

interface SessionTrackingData {
  VideoId: string;
  TimeWatched: number;
  IsPlaying: boolean;
  CurrentTime: number;
  VideoLibraryId: number;
  TimeToFirstFrame: number | null;
  ZoneTier?: string | null;
  ServerId?: string;
  SessionId?: string;
}

interface TrackingConfig {
  videoId: string;
  videoLibraryId: number;
  serverId?: string;
  trackingApiUrl: string;
  iframeDomain: string;
  collectionInterval?: number;
}

const ATTR_VIDEO_ID = 'video-id' as const;
const ATTR_VIDEO_LIBRARY_ID = 'video-library-id' as const;
const ATTR_SERVER_ID = 'server-id' as const;
const ATTR_TRACKING_API_URL = 'tracking-api-url' as const;
const ATTR_IFRAME_DOMAIN = 'iframe-domain' as const;
const ATTR_COLLECTION_INTERVAL = 'collection-interval' as const;
const ATTR_DISABLED = 'disabled' as const;

const DEFAULT_COLLECTION_INTERVAL = 5000; // 5 seconds

/**
 * Analytics tracking component for Bunny Stream player
 *
 * This component integrates with Media Chrome to track video player analytics
 * and send data to the Bunny Stream tracking API. It listens to Media Chrome
 * events to capture both media state changes and user interactions.
 *
 * @attr {string} video-id - The video ID to track
 * @attr {string} video-library-id - The video library ID
 * @attr {string} server-id - Optional server ID
 * @attr {string} tracking-api-url - API endpoint for tracking data
 * @attr {string} iframe-domain - Domain for CORS handling
 * @attr {number} collection-interval - Data collection interval in ms (default: 5000)
 * @attr {boolean} disabled - Disable analytics tracking
 */
export class BunnyStreamInsights extends HTMLElement {
  private mediaController: MediaController | null = null;

  // Tracking state
  private sessionId: string | null = null;
  private startTime: number = 0;
  private untrackedTime: number = 0;
  private currentVideoProgress: number = 0;
  private isPlaying: boolean = false;
  private firstPlayTracked: boolean = false;
  private timeToFirstFrameTracked: boolean = false;
  private timeToFirstFrameCollected: boolean = false;
  private timeToFirstFrameMs: number = 0;
  private playEventTimeMs: number = 0;
  private collectionIntervalId: number | null = null;

  // Configuration
  private config: TrackingConfig | null = null;
  private initialized: boolean = false;

  static get observedAttributes(): string[] {
    return [
      ATTR_VIDEO_ID,
      ATTR_VIDEO_LIBRARY_ID,
      ATTR_SERVER_ID,
      ATTR_TRACKING_API_URL,
      ATTR_IFRAME_DOMAIN,
      ATTR_COLLECTION_INTERVAL,
      ATTR_DISABLED,
    ];
  }

  constructor() {
    super();
  }

  connectedCallback(): void {
    if (this.disabled) {
      return;
    }

    this.findMediaController();

    if (this.hasRequiredAttributes()) {
      this.initialize();
    }
  }

  disconnectedCallback(): void {
    this.cleanup();
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return;

    if (name === ATTR_DISABLED) {
      if (this.disabled) {
        this.cleanup();
      } else if (this.hasRequiredAttributes()) {
        this.initialize();
      }
      return;
    }

    // Re-initialize if core attributes change
    const coreAttributes = [
      ATTR_VIDEO_ID,
      ATTR_VIDEO_LIBRARY_ID,
      ATTR_TRACKING_API_URL,
      ATTR_IFRAME_DOMAIN,
    ] as const;

    if (coreAttributes.includes(name as typeof coreAttributes[number])) {
      if (this.initialized) {
        this.cleanup();
        if (this.hasRequiredAttributes() && !this.hasAttribute(ATTR_DISABLED)) {
          this.initialize();
        }
      }
    }

    // Update collection interval if changed
    if (name === ATTR_COLLECTION_INTERVAL && this.collectionIntervalId) {
      this.restartCollectionInterval();
    }
  }

  private findMediaController(): void {
    this.mediaController = (this.closest('media-controller') ||
      this.parentElement?.querySelector('media-controller') ||
      document.querySelector('media-controller')) as MediaController;

    if (!this.mediaController) {
      console.warn('Insights: No media-controller found');
      return;
    }
  }

  private hasRequiredAttributes(): boolean {
    return !!(
      this.getAttribute(ATTR_VIDEO_ID) &&
      this.getAttribute(ATTR_VIDEO_LIBRARY_ID) &&
      this.getAttribute(ATTR_TRACKING_API_URL) &&
      this.getAttribute(ATTR_IFRAME_DOMAIN)
    );
  }

  private initialize(): void {
    if (this.initialized || !this.mediaController) {
      return;
    }

    this.config = this.buildConfig();
    if (!this.config) {
      console.error('Insights: Invalid configuration');
      return;
    }

    this.resetTrackingState();
    this.attachEventListeners();
    this.initialized = true;
  }

  private buildConfig(): TrackingConfig | null {
    const videoId = this.getAttribute(ATTR_VIDEO_ID);
    const videoLibraryIdStr = this.getAttribute(ATTR_VIDEO_LIBRARY_ID);
    const trackingApiUrl = this.getAttribute(ATTR_TRACKING_API_URL);
    const iframeDomain = this.getAttribute(ATTR_IFRAME_DOMAIN);

    if (!videoId || !videoLibraryIdStr || !trackingApiUrl || !iframeDomain) {
      return null;
    }

    const videoLibraryId = parseInt(videoLibraryIdStr, 10);
    if (isNaN(videoLibraryId)) {
      console.error('Insights: Invalid video-library-id');
      return null;
    }

    const collectionIntervalStr = this.getAttribute(ATTR_COLLECTION_INTERVAL);
    const collectionInterval = collectionIntervalStr
      ? parseInt(collectionIntervalStr, 10)
      : DEFAULT_COLLECTION_INTERVAL;

    return {
      videoId,
      videoLibraryId,
      serverId: this.getAttribute(ATTR_SERVER_ID) || undefined,
      trackingApiUrl,
      iframeDomain,
      collectionInterval: isNaN(collectionInterval)
        ? DEFAULT_COLLECTION_INTERVAL
        : collectionInterval,
    };
  }

  private resetTrackingState(): void {
    this.sessionId = this.generateSessionId();
    this.startTime = 0;
    this.untrackedTime = 0;
    this.currentVideoProgress = 0;
    this.isPlaying = false;
    this.firstPlayTracked = false;
    this.timeToFirstFrameTracked = false;
    this.timeToFirstFrameCollected = false;
    this.timeToFirstFrameMs = 0;
    this.playEventTimeMs = 0;
  }

  private attachEventListeners(): void {
    if (!this.mediaController) return;

    // Listen to Media Chrome state change events
    this.mediaController.addEventListener(
      'mediapaused',
      this.handleMediaPausedChange,
    );
    this.mediaController.addEventListener(
      'mediacurrenttime',
      this.handleMediaCurrentTimeChange,
    );
    this.mediaController.addEventListener(
      'mediaended',
      this.handleMediaEndedChange,
    );
    this.mediaController.addEventListener(
      'mediahasplayed',
      this.handleMediaHasPlayedChange,
    );
    this.mediaController.addEventListener(
      'medialoading',
      this.handleMediaLoadingChange,
    );
  }

  private detachEventListeners(): void {
    if (!this.mediaController) return;

    // Remove Media Chrome state change events
    this.mediaController.removeEventListener(
      'mediapaused',
      this.handleMediaPausedChange,
    );
    this.mediaController.removeEventListener(
      'mediacurrenttime',
      this.handleMediaCurrentTimeChange,
    );
    this.mediaController.removeEventListener(
      'mediaended',
      this.handleMediaEndedChange,
    );
    this.mediaController.removeEventListener(
      'mediahasplayed',
      this.handleMediaHasPlayedChange,
    );
    this.mediaController.removeEventListener(
      'medialoading',
      this.handleMediaLoadingChange,
    );
  }

  // Media Chrome State Change Event Handlers
  private handleMediaPausedChange = (event: Event): void => {
    const detail = (event as CustomEvent).detail;
    const isPaused = detail === true;

    if (isPaused && this.isPlaying) {
      // Video was paused
      this.isPlaying = false;
      this.increaseUntrackedTime();
      this.stopCollectionInterval(); // Stop interval when paused
    } else if (!isPaused && !this.isPlaying) {
      // Video was played
      this.startTime = this.getCurrentTimestamp();
      this.isPlaying = true;
      this.playEventTimeMs = this.getCurrentTimeMs();

      if (!this.firstPlayTracked) {
        this.firstPlayTracked = true;
      }

      // Always ensure interval is running when playing starts
      this.startCollectionInterval();
    }
  };

  private handleMediaCurrentTimeChange = (event: Event): void => {
    const detail = (event as CustomEvent).detail;
    this.currentVideoProgress = detail || 0;

    if (this.isPlaying) {
      this.increaseUntrackedTime();
    }

    // Track first frame after play starts
    if (
      !this.timeToFirstFrameTracked && this.playEventTimeMs > 0 &&
      this.isPlaying
    ) {
      this.timeToFirstFrameTracked = true;
      this.timeToFirstFrameMs = this.getCurrentTimeMs() - this.playEventTimeMs;
    }
  };

  private handleMediaEndedChange = (event: Event): void => {
    const detail = (event as CustomEvent).detail;
    if (detail === true) {
      this.isPlaying = false;
      this.increaseUntrackedTime();
      this.sendTrackingData(); // Send final data
      this.stopCollectionInterval(); // Stop periodic tracking
    }
  };

  private handleMediaHasPlayedChange = (event: Event): void => {
    const detail = (event as CustomEvent).detail;
    if (detail === true && !this.firstPlayTracked) {
      this.firstPlayTracked = true;
      this.startCollectionInterval();
    }
  };

  private handleMediaLoadingChange = (event: Event): void => {
    const detail = (event as CustomEvent).detail;
    if (detail === true) {
      // Reset tracking state when new media starts loading
      this.resetTrackingState();
    }
  };

  private increaseUntrackedTime(): void {
    const currentTime = this.getCurrentTimestamp();
    if (currentTime > this.startTime) {
      this.untrackedTime += currentTime - this.startTime;
      this.startTime = currentTime;
    }
  }

  private startCollectionInterval(): void {
    if (this.collectionIntervalId || !this.config) return;

    this.collectionIntervalId = globalThis.setInterval(() => {
      this.sendTrackingData();
    }, this.config.collectionInterval);
  }

  private restartCollectionInterval(): void {
    this.stopCollectionInterval();
    this.startCollectionInterval();
  }

  private stopCollectionInterval(): void {
    if (this.collectionIntervalId) {
      clearInterval(this.collectionIntervalId);
      this.collectionIntervalId = null;
    }
  }

  private async sendTrackingData(): Promise<void> {
    if (!this.config || this.untrackedTime === 0) return;

    const postObject: SessionTrackingData = {
      VideoId: this.config.videoId,
      TimeWatched: this.untrackedTime,
      IsPlaying: this.isPlaying,
      CurrentTime: Math.floor(this.currentVideoProgress),
      VideoLibraryId: this.config.videoLibraryId,
      TimeToFirstFrame: null,
      SessionId: this.sessionId || undefined,
    };

    if (this.config.serverId) {
      postObject.ServerId = this.config.serverId;
    }

    if (this.timeToFirstFrameTracked && !this.timeToFirstFrameCollected) {
      postObject.TimeToFirstFrame = this.timeToFirstFrameMs;
      this.timeToFirstFrameCollected = true;
    }

    this.untrackedTime = 0;

    try {
      const response = await fetch(this.config.trackingApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(postObject),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Insights: Failed to send tracking data', error);

      this.untrackedTime += postObject.TimeWatched;
    }
  }

  // Centralized time functions for easier testing
  // These methods can be easily mocked in unit tests
  private getCurrentTimeMs(): number {
    return Date.now();
  }

  private getCurrentTimestamp(): number {
    return Math.round(this.getCurrentTimeMs() / 1000);
  }

  private generateSessionId(): string {
    return `${this.getCurrentTimestamp()}-${
      Math.random().toString(36).substring(2, 11)
    }`;
  }

  private cleanup(): void {
    this.detachEventListeners();
    this.stopCollectionInterval();
    this.initialized = false;
  }

  // Public API
  get videoId(): string | null {
    return this.getAttribute(ATTR_VIDEO_ID);
  }

  set videoId(value: string | null) {
    if (value) {
      this.setAttribute(ATTR_VIDEO_ID, value);
    } else {
      this.removeAttribute(ATTR_VIDEO_ID);
    }
  }

  get videoLibraryId(): number | null {
    const value = this.getAttribute(ATTR_VIDEO_LIBRARY_ID);
    return value ? parseInt(value, 10) : null;
  }

  set videoLibraryId(value: number | null) {
    if (value && typeof value === 'number' && value > 0) {
      this.setAttribute(ATTR_VIDEO_LIBRARY_ID, value.toString());
    } else {
      this.removeAttribute(ATTR_VIDEO_LIBRARY_ID);
    }
  }

  get disabled(): boolean {
    const value = this.getAttribute(ATTR_DISABLED);
    return value !== null && value !== 'false';
  }

  set disabled(value: boolean) {
    if (value) {
      this.setAttribute(ATTR_DISABLED, '');
    } else {
      this.removeAttribute(ATTR_DISABLED);
    }
  }

  public flushTrackingData(): Promise<void> {
    return this.sendTrackingData();
  }

  public getTrackingState(): object {
    return {
      sessionId: this.sessionId,
      isPlaying: this.isPlaying,
      currentTime: this.currentVideoProgress,
      untrackedTime: this.untrackedTime,
      firstPlayTracked: this.firstPlayTracked,
      timeToFirstFrame: this.timeToFirstFrameMs,
      initialized: this.initialized,
    };
  }
}

if (!globalThis.customElements.get('bunny-stream-insights')) {
  globalThis.customElements.define(
    'bunny-stream-insights',
    BunnyStreamInsights,
  );
}

export default BunnyStreamInsights;
